import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output,
    ViewChild, Inject, AfterViewInit, SimpleChanges } from '@angular/core';
import { APP_BASE_HREF, DecimalPipe } from '@angular/common';
import { animate, AnimationFactory, state, style, transition, trigger } from '@angular/animations';

import { Subscription, filter } from 'rxjs';
import { cloneDeep } from 'lodash';

import { MediaTypeEnum } from '@Common/Basic/enums/media-type.enum';
import { LinkedinSubTypeEnum } from '@Common/Basic/enums/linkedin-sub-type.enum';
import { TVEyesArchivedStateEnum } from '../../../../api/mentions/enum/tv-eyes-archived-state.enum';
import { IndexHighlightPipe } from '../../../../Common/Misc/pipes/Highlight/indexHighlight.pipe';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { PreferencesModel } from '../../../../Core/Users/models/preferences.model';
import { CurrentUserStateService, TargetingAccessLevel } from '../../../../Core/Users/services/current-user-state.service';
import { ButtonTypesEnum } from '../../../Forms/enums/button-types.enum';
import { getTextAlign } from '../../../Misc/helpers/formatting/text.helper';
import { KeywordHighlightModel } from '../../../Misc/models/keyword-highlight.model';
import { TVEyesMentionsService } from '../../services/tveyes-mentions.service';
import { MentionContentCustomizationEnum } from '../../enums/mention-content-customization.enum';
import { Mention } from '../../models/mention';
import { ActionKeys } from '../../../Forms/enums/keys.enum';
import { TranslateMentionsService } from '../../../Mentions/services/translate-mentions.service';
import { Folder } from '../../../../Monitor/Shared/models/folder.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { SearchCriteriaSortFieldEnum } from '@api/mentions/enum/search-criteria-sort-field.enum';
import { attributeTypesEnum } from '../../../Misc/enums/attribute-types.enum';
import { OptionModel } from '../../../Forms/models/dropdown-option.model';
import { MentionSearchModel } from '../../../../Monitor/Shared/models/mention-search.model';

export enum BadgeSectionEnum {
    first,
    second
}

export enum MentionBadgeTypeEnum {
    MENTION_SAVED_BADGE = 'MENTION_SAVED_BADGE',
    MENTION_EDITED_BADGE = 'MENTION_EDITED_BADGE',
    MENTION_HAS_IMAGES_BADGE = 'MENTION_HAS_IMAGES_BADGE',
    MENTION_IS_ARCHIVED_MENTION = 'MENTION_IS_ARCHIVED_MENTION',
    MENTION_IS_ARCHIVE_PROCESSING_MENTION = 'MENTION_IS_ARCHIVE_PROCESSING_MENTION',
    MENTION_IN_SELECTED_FOLDER = 'MENTION_IN_SELECTED_FOLDER',
    MENTION_IN_REPORT = 'MENTION_IN_REPORT',
    MENTION_SAVED_INSIGHT = 'MENTION_SAVED_INSIGHT',
    MENTION_IN_BRIEFING = 'MENTION_IN_BRIEFING',
}

export interface MentionBadge {
    type: MentionBadgeTypeEnum;
    icon: string;
    tooltip: string;
    style?: { [property: string]: any };
    section: BadgeSectionEnum;
    condition: (mention: Mention, selectedFolder?: Folder) => boolean;
}

export const mentionSavedBadge: MentionBadge = {
    type: MentionBadgeTypeEnum.MENTION_SAVED_BADGE,
    icon: 'fa-kit-duotone fa-agpr-check-save',
    tooltip: 'Mention.badge.saved.tooltip',
    section: BadgeSectionEnum.second,
    condition: (mention: Mention) => {
        return !!mention.mentionId;
    }
};

export const mentionEditedBadge: MentionBadge = {
    type: MentionBadgeTypeEnum.MENTION_EDITED_BADGE,
    icon: 'fa-kit-duotone fa-agpr-check-edit',
    tooltip: 'Mention.badge.edited.tooltip',
    section: BadgeSectionEnum.first,
    condition: (mention: Mention) => {
        return (mention.isArchivedMention && mention.tvEyesArchiveState === TVEyesArchivedStateEnum.EDITED) || mention.isEdited;
    }
};

export const mentionHasImagesBadge: MentionBadge = {
    type: MentionBadgeTypeEnum.MENTION_HAS_IMAGES_BADGE,
    icon: 'fa-regular fa-duotone fa-image',
    tooltip: 'Mention.badge.hasImages.tooltip',
    section: BadgeSectionEnum.first,
    condition: (mention: Mention) => {
        return mention.images && mention.images.length > 0;
    }
};

export const mentionIsArchivedMention: MentionBadge = {
    type: MentionBadgeTypeEnum.MENTION_IS_ARCHIVED_MENTION,
    icon: 'fa-regular fa-duotone fa-box-archive',
    tooltip: 'Mention.badge.archivedMention',
    section: BadgeSectionEnum.first,
    condition: (mention: Mention) => {
        return mention.isArchivedMention && mention.tvEyesArchiveState === TVEyesArchivedStateEnum.COMPLETED;
    }
};

export const mentionIsArchiveProcessingMention: MentionBadge = {
    type: MentionBadgeTypeEnum.MENTION_IS_ARCHIVE_PROCESSING_MENTION,
    icon: 'fa-regular fa-duotone fa-boxes-packing',
    tooltip: 'Mention.badge.processing.tooltip',
    section: BadgeSectionEnum.first,
    condition: (mention: Mention) => {
        return mention.isArchivedMention && mention.tvEyesArchiveState === TVEyesArchivedStateEnum.PROCESSING;
    }
};

export const mentionSavedInsightBadge: MentionBadge = {
    type: MentionBadgeTypeEnum.MENTION_SAVED_INSIGHT,
    icon: 'fa-kit-duotone fa-agpr-check-insight',
    tooltip: 'Mention.badge.savedInsight.tooltip',
    section: BadgeSectionEnum.first,
    condition: (mention: Mention) => {
        return mention.insights?.length > 0;
    }
};

export const mentionInSelectedFolders: MentionBadge = {
    type: MentionBadgeTypeEnum.MENTION_IN_SELECTED_FOLDER,
    icon: 'fa-kit-duotone fa-agpr-check-folder',
    tooltip: 'Mention.badge.mentionInFolders.tooltip',
    section: BadgeSectionEnum.second,
    condition: (mention: Mention, selectedFolder: Folder) => {
        return selectedFolder && mention.folderIds.length && mention.folderIds.some(id => id === selectedFolder.id);
    }
};


export const mentionBadgesAll = [
    mentionSavedBadge,
    mentionIsArchivedMention,
    mentionIsArchiveProcessingMention,
    mentionEditedBadge,
    mentionHasImagesBadge,
    mentionSavedInsightBadge,
    mentionInSelectedFolders
];

export const mentionBadgesSaved = [
    mentionIsArchivedMention,
    mentionIsArchiveProcessingMention,
    mentionEditedBadge,
    mentionHasImagesBadge,
    mentionSavedInsightBadge,
    mentionInSelectedFolders
];


@Component({
    selector: 'agpr-mention-card',
    templateUrl: './mention-card.component.html',
    styleUrls: ['./mention-card.component.scss'],
    animations: [
        trigger('showMoreContent', [
            state('collapsed', style({
                height: '{{collapsedHeight}}',
            }), { params: { collapsedHeight: '*' } }),
            state('expanded', style({
                height: '*',
            })),
            transition('expanded <=> collapsed', animate('800ms 100ms cubic-bezier(0.42, 0, 0.58, .99)'))
        ])
    ],
    standalone: false
})

export class MentionCardComponent implements OnChanges, OnInit, OnDestroy, AfterViewInit {

    @Input() id: string;
    @Input() highlightKeywords: KeywordHighlightModel[];
    @Input() badges: MentionBadge[] = mentionBadgesAll;
    @Input() mentionContent: MentionContentCustomizationEnum  = MentionContentCustomizationEnum.summary;

    @Input() mention: Mention;
    @Input() mentionSearch: MentionSearchModel;
    @Input() canExpand: boolean = true;

    @Input() set showImages(isShowImages: boolean) {
        this._showImages = isShowImages;
        this.updateCollapsedHeight();
    };

    @Input() set compact(isCompact: boolean) {
        this._compact = isCompact;
        this.updateCollapsedHeight();
    };

    // HACK for collapsedHeight.. because if in a tab view, and it's not the first / visible tab, the height it zero..
    @Input() set reGetCardHeight(reGetCardHeight: boolean) {
        if (reGetCardHeight) {
            this.updateCollapsedHeight();
        }
    };
    @Input() showPublishedDate: boolean = true;
    @Input() showSentimentOnExpanded: boolean = true;
    @Input() selectedFolder: Folder;
    // Metric type to show the value in the right column
    @Input() metricType: SearchCriteriaSortFieldEnum = SearchCriteriaSortFieldEnum.audience_reach;
    @Input() useMentionCardErrorTemplate: boolean;
    @Input() isSyndicateMode: boolean = false;
    @Input() enablePrecisionClip: boolean = false;

    @Output() outletClicked: EventEmitter<void> = new EventEmitter<void>();
    @Output() contactClicked: EventEmitter<void> = new EventEmitter<void>();
    @Output() unlockMentionClicked: EventEmitter<void> = new EventEmitter<void>();
    @Output() viewAllSyndicatesClicked: EventEmitter<void> = new EventEmitter<void>();

    @ViewChild('mainBody', { read: ElementRef }) mainBody: ElementRef;
    @ViewChild('cardContainer') cardContainer: ElementRef;

    collapsedHeight: string;

    _showImages: boolean = false;
    _compact: boolean = false;
    canLinkOut: boolean;
    activeBadges: MentionBadge[];
    expanded: boolean = false;
    contentVisible: boolean = false;
    isLegacyTargetingUser: boolean = false;
    paragraphs: string[] = [];
    preferences: PreferencesModel;
    bodyTextAlign: string = 'left';
    isTVEyes: boolean = false;
    expandAnimation: AnimationFactory;
    collapseAnimation: AnimationFactory;
    enableExpandToggle: boolean = true;
    dateFormat: string;
    similarWebTooltip: string;
    engagementTooltip: string;

    protected subscriptions: Subscription[] = [];

    readonly buttonTypes = ButtonTypesEnum;
    readonly mediaTypes = MediaTypeEnum;
    readonly linkedinTypeEnum = LinkedinSubTypeEnum;
    readonly mentionContentTypes = MentionContentCustomizationEnum;
    readonly maxWordCount: number = 30;
    readonly badgeSections = BadgeSectionEnum;
    readonly searchCriteriaSortFieldEnum = SearchCriteriaSortFieldEnum;
    readonly attributeTypes = attributeTypesEnum;

    constructor(
        protected currentUserStateService: CurrentUserStateService,
        protected coreDialogService: CoreDialogService,
        protected indexHighlightPipe: IndexHighlightPipe,
        protected tvEyesMentionsService: TVEyesMentionsService,
        protected translateService: TranslateMentionsService,
        protected localizationService: LocalizationService,
        protected decimalPipe: DecimalPipe,
        protected changeDetectorRef: ChangeDetectorRef,
        @Inject (APP_BASE_HREF) public appBaseHref: string
    ) { }

    ngOnInit() {
        this.subscriptions.push(
            this.currentUserStateService.getPreferences().pipe(filter(x => !!x)).subscribe(
                prefs => {
                    this.preferences = prefs;
                    this.dateFormat = prefs.getDateFormatString(true, true, true);
                }
            ),
            this.currentUserStateService.getTargetingAccessLevel().subscribe(x => {
                this.isLegacyTargetingUser = x === TargetingAccessLevel.CLASSIC_ONLY;
            }),
            this.translateService.getTextAlign().subscribe((textAlign) => {
                if (textAlign) {
                    this.bodyTextAlign = textAlign;
                }
            }),
            this.translateService.mentionTranslationComplete$.pipe(
                filter(id => !!id && (id === this.mention.id || id === this.mention.mentionId))
            ).subscribe(() => {
                if (!this.expanded) {
                    setTimeout(() => {
                        this.changeDetectorRef.detectChanges();
                        const el = this.cardContainer.nativeElement;
                        const savedHeight = el.style.height;
                        el.style.height = 'auto';
                        const naturalHeight = el.offsetHeight;
                        el.style.height = savedHeight;
                        this.collapsedHeight = naturalHeight + 'px';
                    });
                }
            }),
        );

        if (this.mention?.isTvEyes) {
            this.isTVEyes = this.mention?.isTvEyes();
        }

        this.bodyTextAlign = getTextAlign(this.mention?.body);
        this.setExpandToggle();

        this.similarWebTooltip = this.mention?.isAudienceFromSimilarWeb ? this.localizationService.transform(
            'Common.audienceReach.similarweb.tooltip.insightsBy',
            {src: this.appBaseHref + 'assets/images/SimilarWeb-logo-with-text.svg'}
        ) : null;
    }

    ngAfterViewInit(): void {
        setTimeout(() => {
            this.collapsedHeight = this.getCollapsedHeight();
        });
    }

    ngOnChanges(changes: SimpleChanges) {
        this.setCanLinkOut();
        const isFullText = !this.mention?.body.endsWith('...');

        if (this.mention && this.highlightKeywords) {
            let highlightedBody;

            if (this.mention.summary && !isFullText && this.mentionContent === MentionContentCustomizationEnum.summary) {
                highlightedBody = this.indexHighlightPipe.transform(this.mention.summary, this.mention.summaryContextualKeywordIndices, this.highlightKeywords);
            } else {
                highlightedBody = this.indexHighlightPipe.transform(this.mention.body, this.mention.contexualKeywordIndices, this.highlightKeywords);
            }

            this.paragraphs = (highlightedBody || '').split('\n').filter(s => s !== '');
        } else if (!isFullText && this.mention.summary && this.mentionContent === MentionContentCustomizationEnum.summary) {
            this.paragraphs = (this.mention.summary || '').split('\n').filter(s => s !== '');
        }  else {
            this.paragraphs = (this.mention.body || '').split('\n').filter(s => s !== '');;
        }

        this.updateBadges();
        this.setExpandToggle();
        if (changes.hasOwnProperty('mention')) {
            this.setEngagementTooltip();
        }
    }

    setExpandToggle() {
        this.enableExpandToggle = this.canExpand && (
            // there is an image
            !!this.mention.images.length ||
            // mention body exceeds max word count
            this.mention.body.split(' ').length > this.maxWordCount ||
            // is cloapsed
            this.compact ||
            // is a Finacial Times mention
            this.mention.isFinancialTimes ||
            // is a tweet reply or quote with original text
            !!this.mention.tweetData?.originalTweetData?.originalText?.length
        );
    }

    /**
     * Setter for if the mention headline should link to an outside page
     */
    private setCanLinkOut(): void {
        this.canLinkOut = true;

        // @TODO - NEP-2349 remove when adding DMV
        if (this.mention.isLexisNexis) {
            this.canLinkOut = false;

            return;
        }
        // END of TODO

        if (
            this.mention.mediaType.id === MediaTypeEnum.TV
            || this.mention.mediaType.id === MediaTypeEnum.RADIO
        ) {
            if (Mention.isCriticalMention(this.mention)) {
                this.canLinkOut = false;
            }
        }
    }

    updateBadges(): void {
        if (!this.mention || !this.badges) {
            this.activeBadges = [];
            return;
        }
        this.activeBadges = this.badges.filter((badge) => {
            return badge.condition(this.mention);
        });

        if (this.badges.some(badge => badge.type === MentionBadgeTypeEnum.MENTION_IN_SELECTED_FOLDER)
            && mentionInSelectedFolders.condition(this.mention, this.selectedFolder)
        ) {
            const mentionInFolderBadge: MentionBadge = cloneDeep(mentionInSelectedFolders);
            mentionInFolderBadge.tooltip = this.localizationService.transform(mentionInFolderBadge.tooltip) + this.selectedFolder.name;

            this.activeBadges.push(mentionInFolderBadge);
        }
    }

    toggleExpanded(clickEvent: any): void {
        clickEvent.stopPropagation();
        if (!this.enableExpandToggle) {
            return;
        }
        this.expanded = !this.expanded;

        if (this.expanded) {
            this.contentVisible = this.expanded;
        }
    }

    openTvEyesPodcastPlayer(): void {
        this.tvEyesMentionsService.openTvEyesPodcastPlayer(this.mention.tvEyesPodcastID, this.highlightKeywords);
    }

    openTVEyesDialog(): void {
        if (!this.mention.url) {
            return;
        }

        const keywords: string[] = this.highlightKeywords
            ?.flatMap(x => x.keywords) ?? [];

        this.tvEyesMentionsService.openTVEyesDialog(this.mention, this.mentionSearch, keywords, this.enablePrecisionClip).subscribe();
    }

    onKeydown(event: KeyboardEvent) {
        if (event.key === ActionKeys.ENTER || event.key === ActionKeys.SPACE) {
            this.toggleExpanded(event);
        }
    }

    updateCollapsedHeight(): void {
        this.collapsedHeight = '*';

        setTimeout(() => {
            this.collapsedHeight = this.getCollapsedHeight();
        });
    }

    private getCollapsedHeight(): string {
        const measuredHeight = this.measureContentHeight();
        const minHeight = this.isSyndicateMode ? 165 : 0;
        return Math.max(measuredHeight, minHeight) + 'px';
    }

    private measureContentHeight(): number {
        if (!this.contentVisible) {
            return this.cardContainer.nativeElement.offsetHeight;
        }

        this.contentVisible = false;
        this.changeDetectorRef.detectChanges();
        const height = this.cardContainer.nativeElement.offsetHeight;
        this.contentVisible = true;
        this.changeDetectorRef.detectChanges();

        return height;
    }

    setEngagementTooltip() {
        const likesOption    = new OptionModel('Mention.details.likes', this.mention.like);
        const upvotesOption  = new OptionModel('Mention.details.upvotes', this.mention.like);
        const commentsOption = new OptionModel('Mention.details.comments', this.mention.reply);
        const repliesOption  = new OptionModel('Mention.details.replies', this.mention.reply);
        const sharesOption   = new OptionModel('Mention.details.shares', this.mention.share);
        const repostsOption  = new OptionModel('Mention.details.retweets', this.mention.share);
        const quotesOption   = new OptionModel('Mention.details.quotes', this.mention.quote);

        const engagementInformation: OptionModel[] = [];
        switch (this.mention.mediaType.id) {
            case MediaTypeEnum.TWITTER:
                engagementInformation.push(
                    repostsOption,
                    quotesOption,
                    repliesOption,
                    likesOption
                );
                break;
            case MediaTypeEnum.INSTAGRAM:
                engagementInformation.push(
                    sharesOption,
                    commentsOption,
                    likesOption
                );
                break;
            case MediaTypeEnum.REDDIT:
                engagementInformation.push(
                    sharesOption,
                    commentsOption,
                    upvotesOption
                );
                break;
            case MediaTypeEnum.LINKEDIN:
                engagementInformation.push(
                    commentsOption,
                    likesOption
                );
                break;
            case MediaTypeEnum.BLUESKY:
                engagementInformation.push(
                    repostsOption,
                    quotesOption,
                    repliesOption,
                    likesOption
                );
                break;
            default:
                engagementInformation.push(
                    sharesOption,
                    commentsOption,
                    likesOption
                );
        }
        let tooltip = this.localizationService.transform('Mention.details.engagements') + ':\n';
        tooltip += engagementInformation.map(option => `<b>${this.localizationService.transform(option.label)}</b>:`
            + ` ${this.decimalPipe.transform(option.value || 0)}`).join('\n');
        this.engagementTooltip = tooltip;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
